
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Edit, Trash2, Search, Download, Plus, Edit3, Trash } from "lucide-react";

const QuestionsTable = ({
  questions,
  searchTerm,
  setSearchTerm,
  selectedDifficulty,
  setSelectedDifficulty,
  onEdit,
  onDelete,
  isLoading,
  selectedQuestions,
  setSelectedQuestions,
  onBulkDelete,
  onBulkEdit,
  onBulkAdd,
  onDownloadSelected,
  onDownloadAll,
  currentPage,
  pageSize,
  setPageSize
}) => {
  const difficultyColors = {
    easy: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    hard: "bg-red-100 text-red-800"
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedQuestions(questions.map(q => q._id));
    } else {
      setSelectedQuestions([]);
    }
  };

  const handleSelectQuestion = (questionId, checked) => {
    if (checked) {
      setSelectedQuestions([...selectedQuestions, questionId]);
    } else {
      setSelectedQuestions(selectedQuestions.filter(id => id !== questionId));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const isAllSelected = questions.length > 0 && selectedQuestions.length === questions.length;
  const isIndeterminate = selectedQuestions.length > 0 && selectedQuestions.length < questions.length;

  return (
    <div className="space-y-4">
      {/* Search, Filters and Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search questions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by Difficulty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Difficulties</SelectItem>
            <SelectItem value="easy">Easy</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="hard">Hard</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex gap-2">
          <Button
            onClick={onBulkAdd}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Bulk Add
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={onDownloadAll}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download All
          </Button>

          <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(parseInt(value))}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15">15</SelectItem>
              <SelectItem value="30">30</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedQuestions.length > 0 && (
        <div className="flex gap-2 p-3 bg-blue-50 rounded-lg border">
          <span className="text-sm text-blue-700 mr-2">
            {selectedQuestions.length} question(s) selected
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onBulkEdit}
            className="flex items-center gap-1"
          >
            <Edit3 className="h-4 w-4" />
            Bulk Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDownloadSelected}
            className="flex items-center gap-1"
          >
            <Download className="h-4 w-4" />
            Download Selected
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onBulkDelete(selectedQuestions)}
            className="flex items-center gap-1 text-red-600 hover:text-red-700"
          >
            <Trash className="h-4 w-4" />
            Bulk Delete
          </Button>
        </div>
      )}

      {/* Questions Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                  indeterminate={isIndeterminate}
                />
              </TableHead>
              <TableHead className="w-16">S.No</TableHead>
              <TableHead>Question</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Difficulty</TableHead>
              <TableHead>Points</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {questions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                  No questions found
                </TableCell>
              </TableRow>
            ) : (
              questions.map((question, index) => (
                <TableRow key={question._id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedQuestions.includes(question._id)}
                      onCheckedChange={(checked) => handleSelectQuestion(question._id, checked)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    {(currentPage - 1) * pageSize + index + 1}
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <div className="truncate" title={question.question}>
                      {question.question}
                    </div>
                  </TableCell>
                  <TableCell>{question.category}</TableCell>
                  <TableCell>{question.qtyp}</TableCell>
                  <TableCell>
                    {question.difficultyLevel && (
                      <Badge
                        variant="outline"
                        className={difficultyColors[question.difficultyLevel] || "bg-gray-100 text-gray-800"}
                      >
                        {question.difficultyLevel}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{question.value || 0}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {question.tags && question.tags.length > 0 ? (
                        question.tags.map((tag, tagIndex) => (
                          <Badge key={tagIndex} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-gray-400 text-sm">No tags</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(question)}
                        disabled={question.inUse} // ✅ disable edit
                        title={question.inUse ? "Cannot edit: question is in use" : "Edit"}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(question._id)}
                        className="text-red-600 hover:text-red-700"
                        disabled={question.inUse} // ✅ disable delete
                        title={question.inUse ? "Cannot delete: question is in use" : "Delete"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default QuestionsTable;
